import React from 'react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import ChatIcon from '@atlaskit/icon/glyph/comment';
import PeopleIcon from '@atlaskit/icon/glyph/people';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import { Box, Flex, Text, xcss, Stack } from '@atlaskit/primitives';
import Anchor from '@atlaskit/primitives/anchor';
import { useIntl, FormattedMessage } from '@atlassian/jira-intl';
import stepTwoVectorDarkOptimal from '../../../../../common/assets/step_two_vector_dark_optimal.png';
import stepTwoVectorLightOptimal from '../../../../../common/assets/step_two_vector_light_optimal.png';
import { IconBox, IconBoxColor } from '../../../../../common/ui/icon-box';
import { ConfigurationItem } from '../../../../../common/ui/wizard/configuration-item';
import { WizardInfoItem } from '../../../../../common/ui/wizard/info-item';
import { Layout } from '../../../../../common/ui/wizard/layout';
import { messages as commonMessages } from '../../../../../common/ui/wizard/layout/messages';
import { useIntegrationCounts } from '../../../../../services/store/integrations';
import { useUsersAndRolesStore } from '../../../../../services/store/users-and-roles';
import { messages } from './messages';
import type { StepTwoProps } from './types';

export const StepTwo = ({ onAction, onClose }: StepTwoProps) => {
	const { formatMessage } = useIntl();

	const [{ usersWithoutCustomRole, usersWithoutAdminRole }] = useUsersAndRolesStore();

	if (usersWithoutCustomRole.data === null || usersWithoutAdminRole.data === null) {
		// Users should not see this screen if one of the requests is still loading or error
		throw new Error('[Optimal StepTwo] should not be rendered if users are still loading');
	}

	const usersWithoutCustomRoleCount = usersWithoutCustomRole.data.count;
	const usersWithoutAdminRoleCount = usersWithoutAdminRole.data.count;
	const [{ counts: integrationCounts }] = useIntegrationCounts();
	const chatCount = integrationCounts.data?.chatCount || 0;

	const primaryActionButton = (
		<Button onClick={() => onAction((prevState) => prevState + 1)} appearance="primary">
			{formatMessage(commonMessages.nextButtonNonFinal)}
		</Button>
	);

	const secondaryActionButton = (
		<Button onClick={() => onAction((prevState) => prevState - 1)} appearance="default">
			{formatMessage(commonMessages.backButtonNonFinal)}
		</Button>
	);

	return (
		<Layout
			vector={{ light: stepTwoVectorLightOptimal, dark: stepTwoVectorDarkOptimal }}
			currentStep={2}
			actionButtons={{
				primary: primaryActionButton,
				secondary: secondaryActionButton,
			}}
			onClose={onClose}
		>
			<Flex direction="column" rowGap="space.200">
				<Flex rowGap="space.100" direction="column">
					<Heading size="large">{formatMessage(messages.titleNonFinal)}</Heading>
					<Text>{formatMessage(messages.descriptionNonFinal)}</Text>
				</Flex>
				<Stack space="space.100">
					{(usersWithoutCustomRoleCount > 0 || usersWithoutAdminRoleCount > 0) && (
						<WizardInfoItem
							iconBox={<IconBox icon={PeopleIcon} color={IconBoxColor.Teal} withCustomContainer />}
							title={formatMessage(messages.usersRoleTitleNonFinal)}
							description={formatMessage(messages.usersRoleDescriptionNonFinal, {
								adminCount: usersWithoutAdminRoleCount,
								userCount: usersWithoutCustomRoleCount,
							})}
						/>
					)}
					{chatCount > 0 && (
						<WizardInfoItem
							iconBox={<IconBox icon={ChatIcon} color={IconBoxColor.Lime} withCustomContainer />}
							title={formatMessage(messages.chatIntegrationsTitleNonFinal)}
							description={formatMessage(messages.chatIntegrationsDescriptionNonFinal, {
								chatCount,
							})}
						/>
					)}
				</Stack>
				<Stack space="space.100">
					<Heading size="xsmall" as="h2">
						{formatMessage(messages.configurationsTitleNonFinal)}
					</Heading>
					<Flex direction="column" rowGap="space.025" xcss={configurationItemsWrapperStyles}>
						<ConfigurationItem>
							{formatMessage(messages.actionsAndIncidentRulesNonFinal)}
						</ConfigurationItem>
						<ConfigurationItem>{formatMessage(messages.heartbeatsNonFinal)}</ConfigurationItem>
					</Flex>
				</Stack>
				<Box>
					<Anchor
						href="https://support.atlassian.com/jira-service-management-cloud/docs/start-shifting-from-opsgenie-to-jira-service-management"
						target="_blank"
					>
						<FormattedMessage {...messages.readMoreAboutNonFinal} />
						<ShortcutIcon label="" size="small" />
					</Anchor>
				</Box>
			</Flex>
		</Layout>
	);
};

const configurationItemsWrapperStyles = xcss({
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
});
